import api from 'api/api';
import loaderSignal from 'signals/Loader.signal';
import alertSignal from 'signals/Alert.signal';
import edoClaimsTrackingSignal from 'signals/EdoClaimsTracking.signal';

const toIsoStart = (dateStr) => {
  if (!dateStr) return undefined;
  return new Date(`${dateStr}T00:00:00.000Z`).toISOString();
};

const toIsoEnd = (dateStr) => {
  if (!dateStr) return undefined;
  return new Date(`${dateStr}T23:59:59.999Z`).toISOString();
};

/** Build Prisma where for Claims Tracking. Isabel Sep 25: Date Claim Submitted only. Program optional. */
export const buildClaimsTrackingWhere = (filters = {}) => {
  const where = {};

  const programId = Number(filters.fundingProgramId);
  if (programId) {
    where.application = {
      is: {
        fundingProgramId: programId,
      },
    };
  }

  const from = toIsoStart(filters.claimSubmittedDateFrom);
  const to = toIsoEnd(filters.claimSubmittedDateTo);
  if (from || to) {
    where.claimSubmittedDate = {
      ...(from ? { gte: from } : {}),
      ...(to ? { lte: to } : {}),
    };
  }

  return where;
};

export const handleClaimsFilterChange = (name, value) => {
  edoClaimsTrackingSignal.update({
    dropdownFilters: {
      ...edoClaimsTrackingSignal.value.dropdownFilters,
      [name]: value,
    },
  });
};

export const resetClaimsFilterInputs = () => {
  edoClaimsTrackingSignal.update({
    dropdownFilters: {
      fundingProgramId: '',
      claimSubmittedDateFrom: '',
      claimSubmittedDateTo: '',
    },
  });
};

export const applyClaimsFilters = async (page = 1) => {
  const { dropdownFilters } = edoClaimsTrackingSignal.value;
  edoClaimsTrackingSignal.update({
    appliedFilters: { ...dropdownFilters },
  });
  await fetchAndSetClaims(page);
};

export const clearClaimsFilters = async (page = 1) => {
  resetClaimsFilterInputs();
  edoClaimsTrackingSignal.update({ appliedFilters: {} });
  await fetchAndSetClaims(page);
};

export const fetchAndSetClaims = async (page = 1) => {
  const { recordsPerPage, appliedFilters } = edoClaimsTrackingSignal.value;
  const where = buildClaimsTrackingWhere(appliedFilters);

  loaderSignal.update({ isContentLoading: true });
  try {
    const data = await api.get({
      path: '/v2/lossClaims',
      options: {
        getCount: true,
        where,
        take: recordsPerPage,
        skip: page ? (page - 1) * recordsPerPage : 0,
        orderBy: [{ id: 'desc' }],
      },
    });

    const claims = Array.isArray(data)
      ? data
      : data?.lossClaims || [];
    const count = Number(data?.count) || claims.length;

    edoClaimsTrackingSignal.update({
      claims,
      totalCount: count,
      totalPages: count ? Math.ceil(count / recordsPerPage) : 1,
    });
  } catch (error) {
    alertSignal.update({
      type: 'notification',
      error,
      message: error.message || 'Failed to load claims',
    });
  } finally {
    loaderSignal.reset();
  }
};

export const handleDownloadClaimsData = async () => {
  const { appliedFilters } = edoClaimsTrackingSignal.value;
  const where = buildClaimsTrackingWhere(appliedFilters);

  loaderSignal.update({ isContentLoading: true, message: 'Downloading claims...' });
  try {
    await api.get({
      path: '/v2/lossClaims/download',
      options: {
        where,
        orderBy: [{ id: 'desc' }],
      },
      isFileDownload: true,
    });
  } catch (error) {
    alertSignal.update({
      type: 'notification',
      error,
      message: error.message || 'Failed to download claims',
    });
  } finally {
    loaderSignal.reset();
  }
};

/**
 * Added Borrower Name column. BOR_NAME.
 * When misc.dba is set, Business.name is the DBA and contactName is the legal name.
 * Do not write the DBA into this column. No DBA: Business.name is the borrower name.
 */
export const resolveClaimBorrowerName = (claim) => {
  const business = claim?.application?.business;
  if (!business) return '';
  const misc = business.misc && typeof business.misc === 'object' ? business.misc : {};
  const dba = String(misc.dba || '').trim();
  if (dba) return String(business.contactName || '').trim();
  return String(business.name || '').trim();
};

export const resolveClaimLender = (claim) =>
  claim?.application?.lender ||
  claim?.application?.fundingProgramMembership?.lender ||
  claim?.lender ||
  null;

/**
 * Teddy Oct 9. One main source. A SAC add-on is not the cell.
 * Same order as claimsTrackingExport.pickMainFund.
 */
const MAIN_FUND_PRIORITY = ['FCS', 'CS3', 'CSF', 'SED', 'C2', 'S', 'ICR', 'B', 'L'];

const MAIN_FUND_LABELS = {
  FCS: 'SSBCI 2.0 CSP Funds',
  CS3: 'SSBCI 2.0 CSP - SEDI',
  CSF: 'CSP',
  SED: 'SSBCI 2.0 SEDI Funds',
  C2: 'SSBCI 2.0 Federal Funds',
  S: 'State Funds',
  ICR: 'Independent Contributor',
  B: 'Borrower',
  L: 'Lender',
};

const FUND_LABEL_TO_CODE = {
  'ssbci 2.0 csp funds': 'FCS',
  'ssbci 2.0 csp - sedi': 'CS3',
  csp: 'CSF',
  'ssbci 2.0 sedi funds': 'SED',
  'ssbci 2.0 federal funds': 'C2',
  'state funds': 'S',
  'independent contributor': 'ICR',
  borrower: 'B',
  lender: 'L',
  'severely affected - state': 'SAS',
  'csp - sac': 'CSS',
  'ssbci 2.0 csp - sac': 'CS2',
  'seismic safety funds- sac': 'YS',
  'seismic safety funds - sac': 'YS',
};

const normalizeFundLabel = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ');

const fundCodeOf = (fund) => {
  const fromCode = String(fund?.fundTypeCode || '').trim().toUpperCase();
  if (fromCode) return fromCode;
  const blob = fund?.cpcfaImportBlob;
  const fromBlob = blob && typeof blob === 'object' ? blob.FND_TYPE : '';
  if (fromBlob) return String(fromBlob).trim().toUpperCase();
  return FUND_LABEL_TO_CODE[normalizeFundLabel(fund?.fundType)] || '';
};

/** Isabel 2d: one fundType. Do not concat. */
export const resolveClaimFundingType = (claim) => {
  const funds = claim?.application?.funds;
  if (!Array.isArray(funds) || funds.length === 0) return '';
  const candidates = [];
  for (const fund of funds) {
    const rank = MAIN_FUND_PRIORITY.indexOf(fundCodeOf(fund));
    if (rank === -1) continue;
    candidates.push({ fund, rank });
  }
  if (candidates.length === 0) return '';
  candidates.sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    const aTime = a.fund.fundDate ? new Date(a.fund.fundDate).getTime() : 0;
    const bTime = b.fund.fundDate ? new Date(b.fund.fundDate).getTime() : 0;
    if (aTime !== bTime) return aTime - bTime;
    return (a.fund.id || 0) - (b.fund.id || 0);
  });
  const fund = candidates[0].fund;
  const stored = String(fund.fundType || '').trim();
  if (stored) return stored;
  return MAIN_FUND_LABELS[fundCodeOf(fund)] || '';
};

export const formatClaimDate = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC' });
  } catch {
    return '';
  }
};
